import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Typography, styled } from "@mui/material";
import ErrorOutlineOutlined from "@mui/icons-material/ErrorOutlineOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import { ContactSupportDialog } from "./ContactSupportDialog";
import { facilityService } from "@services/facilityServices";
import {
  clearRestrictedFacilitySession,
  isAccessRestrictedFacility,
} from "@utils/facilityRestricted";
import {
  clearLeftoverUnpaidIdentity,
  leftoverFacilityId,
  leftoverPreferredPk,
  persistPreferredFacilityForSelectPlan,
  pickLeftoverUnpaidSource,
  readLeftoverUnpaidIdentity,
  readSelectedPlanIdFromSession,
  resolveRestrictedUpdatePaymentAction,
} from "@utils/leftoverUnpaidCheckout";
import {
  readPreferredFacilityForPricing,
  writePreferredFacilityForPricing,
} from "@utils/pricingFacilityContext";

type RouteState = {
  selectedFacility?: {
    facility_name?: string;
    plan_id?: number | null;
  };
  facilityName?: string;
  hasMultipleFacilities?: boolean;
  restrictedMessageVariant?: "trial_expired" | "payment_failed_expired";
};

const PageContainer = styled(Box)({
  background: "#fdffff",
  borderRadius: "12px",
  height: "97vh",
  display: "flex",
  flexDirection: "column",
  border: "1px solid #99d4e1",
  boxShadow: "8px 2px 58.4px 0px inset rgba(191, 242, 255, 0.2)",
  position: "relative",
  overflow: "hidden",
});

const HeaderContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "rgba(253, 255, 255, 0.2)",
  borderBottom: "1px solid #35d7e4",
  padding: "12px 64px",
  height: "124px",
  display: "flex",
  alignItems: "end",
  gap: "8px",
  position: "sticky",
  top: 0,
  zIndex: 1,
  [theme.breakpoints.down("md")]: {
    padding: "12px 24px",
    height: "96px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "8px 16px",
    height: "80px",
  },
}));

const HeaderTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
});

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "28px",
  fontWeight: 500,
  lineHeight: "1.5",
  color: "#262626",
  letterSpacing: "-1px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "22px",
  },
}));

const ContentSection = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: "32px 48px 16px",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  overflowY: "auto",
  overflowX: "hidden",
  [theme.breakpoints.down("md")]: {
    padding: "24px 24px 16px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "16px 12px 16px",
  },
}));

export default function FacilityRestrictedScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [supportOpen, setSupportOpen] = useState(false);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const routeState = (location.state || {}) as RouteState;

  const facilityName = useMemo(() => {
    const storedFacilityName = sessionStorage.getItem("restricted_facility_name");
    return (
      routeState.selectedFacility?.facility_name ||
      routeState.facilityName ||
      storedFacilityName ||
      "Facility"
    );
  }, [routeState.facilityName, routeState.selectedFacility?.facility_name]);

  const showMultiFacilityText = useMemo(() => {
    if (typeof routeState.hasMultipleFacilities === "boolean") {
      return routeState.hasMultipleFacilities;
    }
    return (
      sessionStorage.getItem("restricted_has_multiple_facilities") === "1"
    );
  }, [routeState.hasMultipleFacilities]);

  useEffect(() => {
    let cancelled = false;

    const verifyAccess = async () => {
      const facilityIdRaw = sessionStorage.getItem("restricted_facility_id");
      const facilityId = facilityIdRaw ? Number(facilityIdRaw) : NaN;
      if (!Number.isFinite(facilityId) || facilityId <= 0) {
        return;
      }

      try {
        const facilities = await facilityService.getMyFacilities({
          bypassCache: true,
        });
        if (cancelled) return;

        const match = facilities.find((facility) => facility.facility_id === facilityId);
        if (!match || isAccessRestrictedFacility(match)) {
          return;
        }

        clearRestrictedFacilitySession();
        navigate("/dashboard/facilities", {
          replace: true,
          state: {
            currentView: "MyFacility",
            facilityId: match.facility_id,
            facilityName: match.facility_name,
            facilityCity: match.city,
          },
        });
      } catch {
        // Keep restricted screen if verification fails.
      }
    };

    void verifyAccess();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const restrictedMessage = useMemo(() => {
    const variantFromState = routeState.restrictedMessageVariant;
    const variantFromStorage = sessionStorage.getItem("restricted_message_variant");
    const variant = variantFromState || variantFromStorage;
    if (variant === "trial_expired") {
      return "Your free trial for this facility has ended. Add a payment method to restore access.";
    }
    if (variant === "payment_failed_expired") {
      return "Your last payment didn't go through. Please update your payment method to restore access.";
    }
    return "Access to this facility is currently unavailable due to a payment issue. Please update billing to restore access.";
  }, [routeState.restrictedMessageVariant]);

  const handleUpdatePayment = () => {
    setIsCreatingCheckout(true);
    try {
      let selectedFacility: Record<string, unknown> | null = null;
      try {
        const raw = sessionStorage.getItem("filtered_selectedFacility");
        selectedFacility = raw ? (JSON.parse(raw) as Record<string, unknown>) : null;
      } catch {
        selectedFacility = null;
      }
      const preferredFacility = readPreferredFacilityForPricing(null);
      const facilityCtx =
        pickLeftoverUnpaidSource(
          readLeftoverUnpaidIdentity(),
          selectedFacility,
          preferredFacility,
          {
            facility_name: facilityName,
            plan_id: routeState.selectedFacility?.plan_id,
          },
        ) || {
          facility_name: facilityName,
          plan_id: routeState.selectedFacility?.plan_id,
        };

      const storedPlanId =
        sessionStorage.getItem("restricted_plan_id") ||
        routeState.selectedFacility?.plan_id ||
        facilityCtx?.plan_id ||
        readSelectedPlanIdFromSession();
      const preferredFacilityId =
        leftoverPreferredPk(facilityCtx) ??
        leftoverFacilityId(facilityCtx) ??
        (Number.isFinite(Number(facilityCtx?.id)) && Number(facilityCtx?.id) > 0
          ? Number(facilityCtx.id)
          : undefined);

      // Keep the same facility identity grace checkout already sends.
      // Only the stored coupon is cleared.
      const nested = (source: Record<string, unknown> | null | undefined) =>
        source && typeof source.facility_details === "object" && source.facility_details
          ? (source.facility_details as Record<string, unknown>)
          : {};
      const firstText = (...values: unknown[]) => {
        for (const value of values) {
          if (typeof value === "string" && value.trim()) return value.trim();
        }
        return undefined;
      };
      const selectedDetails = nested(selectedFacility);
      const preferredDetails = nested(preferredFacility);
      const ctxDetails = nested(facilityCtx as Record<string, unknown>);
      persistPreferredFacilityForSelectPlan({
        ...preferredFacility,
        ...selectedFacility,
        ...facilityCtx,
        facility_name: firstText(
          facilityCtx?.facility_name,
          selectedFacility?.facility_name,
          preferredFacility?.facility_name,
          facilityName,
          ctxDetails.facility_name,
          selectedDetails.facility_name,
          preferredDetails.facility_name,
        ),
        city: firstText(
          facilityCtx?.city,
          selectedFacility?.city,
          preferredFacility?.city,
          ctxDetails.city,
          selectedDetails.city,
          preferredDetails.city,
        ),
        state: firstText(
          facilityCtx?.state,
          selectedFacility?.state,
          preferredFacility?.state,
          ctxDetails.state,
          selectedDetails.state,
          preferredDetails.state,
        ),
        facility_type: firstText(
          facilityCtx?.facility_type,
          selectedFacility?.facility_type,
          preferredFacility?.facility_type,
          ctxDetails.facility_type,
          selectedDetails.facility_type,
          preferredDetails.facility_type,
        ),
      });
      const storedPreferred = readPreferredFacilityForPricing(null);
      if (storedPreferred) {
        const storedDetails =
          storedPreferred.facility_details &&
          typeof storedPreferred.facility_details === "object"
            ? {
                ...(storedPreferred.facility_details as Record<string, unknown>),
                discount_coupon_name: null,
                discount_coupon_id: null,
                discount_end_date: null,
              }
            : storedPreferred.facility_details;
        writePreferredFacilityForPricing({
          ...storedPreferred,
          discount_coupon_name: null,
          discount_coupon_id: null,
          discount_end_date: null,
          facility_details: storedDetails,
        });
      }
      clearLeftoverUnpaidIdentity();
      clearRestrictedFacilitySession();

      const action = resolveRestrictedUpdatePaymentAction({
        selectedPlanId: storedPlanId,
        preferredFacilityId,
      });
      navigate(`${action.pathname}${action.search}`, { state: action.state });
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  return (
    <PageContainer>
      <HeaderContainer>
        <HeaderTitle>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "999px",
              backgroundColor: "#FEE2E2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ErrorOutlineOutlined sx={{ fontSize: 22, color: "#DC2626" }} />
          </Box>
          <Title>Access Restricted</Title>
        </HeaderTitle>
      </HeaderContainer>

      <ContentSection>
        <Box sx={{ maxWidth: 720, width: "100%" }}>
          <Box sx={{ mt: 3 }}>
            <Typography
              sx={{
                fontFamily: '"Rethink Sans", sans-serif',
                fontWeight: 600,
                fontSize: "15px",
                color: "#065B70",
                mb: 1.5,
              }}
            >
              Selected Facility
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 1,
                px: 2,
                mb: 1,
                borderRadius: "8px",
                border: "1px solid #e5e5e5",
                background: "#fff",
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#DC2626",
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontSize: "14px",
                  color: "#262626",
                }}
              >
                {facilityName}
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontSize: "12px",
                  color: "#DC2626",
                  ml: "auto",
                }}
              >
                Payment required
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              background: "#FFF5F5",
              border: "1px solid #FECACA",
              borderRadius: "12px",
              p: 4,
              mt: 3,
            }}
          >
            <Typography
              sx={{
                color: "#262626",
                fontSize: "15px",
                fontFamily: '"Rethink Sans", sans-serif',
                lineHeight: 1.7,
                mb: 2,
              }}
            >
              {restrictedMessage}
            </Typography>

            {showMultiFacilityText && (
              <Typography
                sx={{
                  color: "#4b5563",
                  fontSize: "13px",
                  fontFamily: '"Rethink Sans", sans-serif',
                  lineHeight: 1.6,
                }}
              >
                Other facilities in your account are not affected.
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 4,
              flexWrap: "wrap",
            }}
          >
            <Button
              onClick={handleUpdatePayment}
              disabled={isCreatingCheckout}
              sx={{
                background: "linear-gradient(90deg, #0A829D 0%, #58C2D8 100%)",
                boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.25)",
                borderRadius: "8px",
                padding: "10px 24px",
                fontFamily: '"Rethink Sans", sans-serif',
                fontWeight: 600,
                fontSize: "14px",
                color: "#e6fafc",
                textTransform: "none",
                "&:hover": {
                  background:
                    "linear-gradient(90deg, #086e86 0%, #4ab3c7 100%)",
                },
              }}
            >
              {isCreatingCheckout ? "Processing..." : "Update Payment"}
            </Button>
            <Button
              onClick={() => setSupportOpen(true)}
              sx={{
                background: "#ffffff",
                border: "1px solid #D1D5DB",
                borderRadius: "8px",
                padding: "10px 24px",
                fontFamily: '"Rethink Sans", sans-serif',
                fontWeight: 500,
                fontSize: "14px",
                color: "#262626",
                textTransform: "none",
                "&:hover": {
                  background: "#F9FAFB",
                },
              }}
            >
              Contact Support
            </Button>
          </Box>
        </Box>
      </ContentSection>

      <ContactSupportDialog
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
      />
    </PageContainer>
  );
}
